export { PUBLIC_PLATFORM_FEATURES } from "./publicNavigation/features";
import { getProductNavConfig } from "./publicNavigation/product";
import { getSolutionsNavConfig } from "./publicNavigation/solutions";
import { getTrustNavConfig } from "./publicNavigation/trust";
import { getAboutNavConfig } from "./publicNavigation/about";
import { getResourcesNavConfig } from "./publicNavigation/resources";
import { getCompanyNavConfig } from "./publicNavigation/company";
import { getSignupCtaNavConfig } from "./publicNavigation/account";
import type {
  NavConfig,
  HeaderSection,
  NavTranslate,
  PublicNavLanguage,
  PublicNavDestinations,
} from "./publicNavigation/types";
export type {
  NavConfig,
  NavItem,
  NavSection,
  NavSubmenu,
  HeaderSection,
  NavTranslate,
  PublicNavLanguage,
  PublicNavDestinations,
} from "./publicNavigation/types";

export type PublicNavContext = {
  t: NavTranslate;
  language: PublicNavLanguage;
  isSwedish: boolean;
  destinations: PublicNavDestinations;
};

const localizedHeaderFallbacks: Record<
  PublicNavLanguage,
  Record<string, string>
> = {
  sv: {
    "nav.resources.guides.title": "Guider",
    "nav.resources.articles.title": "Artiklar",
    "nav.resources.comparisons.title": "Jämförelser",
  },
  en: {
    "nav.resources.guides.title": "Guides",
    "nav.resources.articles.title": "Articles",
    "nav.resources.comparisons.title": "Comparisons",
  },
};

/** Header and mobile share the same five hubs: four visitor hubs and Om oss. */
export function getHeaderSections(config: NavConfig): HeaderSection[] {
  return [
    {
      id: "product",
      label: config.product.label,
      href: config.product.overview,
      submenu: config.product.submenu,
    },
    {
      id: "solutions",
      label: config.solutions.label,
      href: config.solutions.href,
      submenu: config.solutions.submenu,
    },
    {
      id: "trust",
      label: config.trust.label,
      href: config.trust.overview,
      submenu: config.trust.submenu,
    },
    {
      id: "resources",
      label: config.resources.label,
      href: config.resources.overview,
      submenu: config.resources.submenu,
    },
    {
      id: "about",
      label: config.about.label,
      href: config.about.overview,
      submenu: config.about.submenu,
    },
  ];
}

function buildPublicNavConfig(
  t: NavTranslate,
  language: PublicNavLanguage,
  destinations: PublicNavDestinations,
): NavConfig {
  const localizedT: NavTranslate = (key, fallback) =>
    t(key, localizedHeaderFallbacks[language][key] ?? fallback);
  const ctx: PublicNavContext = {
    t: localizedT,
    language,
    isSwedish: language === "sv",
    destinations,
  };
  return {
    product: getProductNavConfig(ctx),
    solutions: getSolutionsNavConfig(ctx),
    trust: getTrustNavConfig(ctx),
    about: getAboutNavConfig(ctx),
    resources: getResourcesNavConfig(ctx),
    company: getCompanyNavConfig(ctx),
    signupCta: getSignupCtaNavConfig(ctx),
  };
}

interface TranslationTree {
  [key: string]: string | TranslationTree;
}

function mapPublicNavText(
  value: unknown,
  path: string,
  t: NavTranslate,
): unknown {
  if (Array.isArray(value))
    return value.map((item, index) =>
      mapPublicNavText(item, `${path}.${index}`, t),
    );
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(
    Object.entries(value).map(([key, child]) => {
      const childPath = path ? `${path}.${key}` : key;
      if (
        (key === "label" || key === "description") &&
        typeof child === "string"
      ) {
        return [key, t(`publicNavigation.${childPath}`, child)];
      }
      return [key, mapPublicNavText(child, childPath, t)];
    }),
  );
}

function buildTranslationTree(value: unknown): TranslationTree {
  if (Array.isArray(value))
    return Object.fromEntries(
      value.map((item, index) => [String(index), buildTranslationTree(item)]),
    ) as TranslationTree;
  if (!value || typeof value !== "object") return {};
  const result: TranslationTree = {};
  for (const [key, child] of Object.entries(value)) {
    if ((key === "label" || key === "description") && typeof child === "string")
      result[key] = child;
    else if (child && typeof child === "object")
      result[key] = buildTranslationTree(child);
  }
  return result;
}

/** Canonical i18next resource generated from the same menu factory used by both apps. */
export function getPublicNavigationTranslations(
  language: PublicNavLanguage,
  destinations: PublicNavDestinations,
): TranslationTree {
  const config = buildPublicNavConfig(
    (_key, fallback) => fallback,
    language,
    destinations,
  );
  const navigation = buildTranslationTree(config);
  return {
    publicNavigation: {
      ...navigation,
      cta: {
        analysis:
          language === "sv"
            ? "Prova Caire kostnadsfritt"
            : "Try Caire for free",
      },
      language: {
        swedish: language === "sv" ? "Svenska" : "Swedish",
        english: language === "sv" ? "Engelska" : "English",
      },
      accessibility:
        language === "sv"
          ? {
              navigation: "Huvudnavigation",
              mobileNavigation: "Mobilnavigation",
              menu: "Meny",
              openMenu: "Öppna meny",
              closeMenu: "Stäng meny",
              showSection: "Visa",
            }
          : {
              navigation: "Main navigation",
              mobileNavigation: "Mobile navigation",
              menu: "Menu",
              openMenu: "Open menu",
              closeMenu: "Close menu",
              showSection: "Show",
            },
    },
  };
}

/** Build the localized public header from shared, path-addressed i18next resources. */
export function getPublicNavConfig(
  t: NavTranslate,
  language: PublicNavLanguage,
  destinations: PublicNavDestinations,
): NavConfig {
  return mapPublicNavText(
    buildPublicNavConfig(t, language, destinations),
    "",
    t,
  ) as NavConfig;
}

export {
  getPublicFooterConfig,
  type FooterConfig,
  type FooterSection,
} from "./publicNavigation/footer";
